import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DimensionValueToken } from '@sleed/bizcommerce-api';
import { dimensionValuesApi, binaryRepoImagesApi } from '~/lib/api/client';
import { formToToken, type DimensionValueFormValues } from '~/lib/schemas/dimension-value';

export const dimensionValueKeys = {
	all: ['dimension-values'] as const,
	byDimension: (did: number) => [...dimensionValueKeys.all, 'by-dimension', did] as const,
	byGrouping: (gid: number) => [...dimensionValueKeys.all, 'by-grouping', gid] as const
};

export function useDimensionValuesByDimension(did: number | null) {
	return useQuery<DimensionValueToken[]>({
		queryKey: dimensionValueKeys.byDimension(did ?? 0),
		queryFn: async () => (await dimensionValuesApi.getByDimension(did!)).data.items ?? [],
		enabled: did != null && did > 0
	});
}

/** Values that belong to a given dimension-value grouping (read-only listing for the grouping page). */
export function useDimensionValuesByGrouping(gid: number | null) {
	return useQuery<DimensionValueToken[]>({
		queryKey: dimensionValueKeys.byGrouping(gid ?? 0),
		queryFn: async () => (await dimensionValuesApi.getByDimensionValueGrouping(gid!)).data.items ?? [],
		enabled: gid != null && gid > 0
	});
}

/**
 * Upload the dimension-value image into slot `key` ("1"). The value must already exist (id > 0); the
 * generated client dropped the multipart param, so we attach the File as form-data keyed by the slot
 * subkey (mirrors the status/banner uploads + the legacy BinaryRepoImages/UploadDimensionValue contract).
 */
export async function uploadDimensionValueImage(valueId: number, key: '1', file: File): Promise<void> {
	const fd = new FormData();
	fd.append(key, file);
	await binaryRepoImagesApi.uploadDimensionValue(valueId, key, { data: fd });
}

export function useDimensionValueMutations(did: number | null) {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: dimensionValueKeys.byDimension(did ?? 0) });
	const create = useMutation({
		mutationFn: async (form: DimensionValueFormValues) => (await dimensionValuesApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: DimensionValueFormValues) => (await dimensionValuesApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({
		mutationFn: async (id: number) => (await dimensionValuesApi.remove(id)).data,
		onSuccess: invalidate
	});
	return { create, update, remove };
}
